/**
 * 画面の一覧（`listScreens`）と画面 1 枚の詳細（`getScreenDetail`）の
 * TanStack Query ラッパー。
 *
 * 画面が `rpcClient` を直接叩かないための層である。Query キー（`query-keys.ts`）と入力の
 * 対応をここだけで決める。画面側で `useQuery` を直接書くと、同じ procedure に別のキーが
 * 付いた瞬間にキャッシュが分裂する。
 *
 * 案件を渡すと、一覧はその案件の未確定変更を重ねた姿（`side: 'after'`）を引く。詳細は
 * 変更前（`before`）と変更後（`after`）のどちらの面かを呼び出し側が選ぶ — 案件モードの
 * 画面詳細は両方を引いて左右に並べる。渡さなければ master だけを引く。**案件番号も画面の id も number である**（作り直しの契約）。URL の
 * `?pj=` と `$screenId` は文字列で届くので、number へ直すのは画面の側の仕事である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の一覧と画面 1 枚の詳細を TanStack Query のフックとして画面へ供給する。
 * @graph-connects rpcClient [calls] specGraph.listScreens
 * @graph-connects rpcClient [calls] specGraph.getScreenDetail
 */
import type { ScreenDetailOutput, ScreenListOutput } from '@cortex/domain-spec-console';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useSpecLocale } from '../../i18n/spec-locale.js';
import { rpcClient } from '../../lib/orpc.js';
import { queryKeys } from '../../lib/query-keys.js';
import type { PjSide } from './spec-graph-element-master-queries.js';

/**
 * 画面の一覧を取得する。
 *
 * @param pjNumber 重ねる案件番号。渡さなければ master
 * @returns 画面の行（id の昇順）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の一覧を、master または指定した案件を適用した姿として取得する。
 * @graph-connects rpcClient [calls] specGraph.listScreens
 */
export function useListScreens(pjNumber?: number): UseQueryResult<ScreenListOutput> {
  const locale = useSpecLocale();
  return useQuery({
    queryKey: queryKeys.specGraph.listScreens(locale, pjNumber),
    queryFn: () =>
      rpcClient.specGraph.listScreens({
        ...(locale === undefined ? {} : { locale }),
        ...(pjNumber === undefined ? {} : { pj: { number: pjNumber, side: 'after' as const } }),
      }),
  });
}

/**
 * 画面 1 枚の詳細を取得する。
 *
 * `screenId` が正の整数でないときは問い合わせない。URL の `$screenId` は文字列なので、
 * 数字でないパスを直接開かれると `Number()` が `NaN` になる。そのまま投げると入力の検証で
 * 弾かれた失敗が「取得に失敗した」として画面に出て、**存在しない id を開いた**という本当の
 * 理由が読めなくなる。
 *
 * @param screenId 画面の id。正の整数でなければ問い合わせない
 * @param pjNumber 重ねる案件番号。渡さなければ master
 * @param side 案件の面。既定は変更後（案件を重ねた姿）
 * @param enabled 問い合わせるか。案件がこの面に画面を持たないと分かっているときは止める
 * @returns 画面 1 枚の詳細
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面 1 枚の詳細を、master または指定した案件の変更前・変更後の姿として取得する。
 * @graph-connects rpcClient [calls] specGraph.getScreenDetail
 */
export function useScreenDetail(
  screenId: number,
  pjNumber?: number,
  side: PjSide = 'after',
  enabled = true,
): UseQueryResult<ScreenDetailOutput> {
  const locale = useSpecLocale();
  const sideKey = pjNumber === undefined ? null : side;
  return useQuery({
    queryKey: queryKeys.specGraph.getScreenDetail(screenId, locale, pjNumber, sideKey),
    queryFn: () =>
      rpcClient.specGraph.getScreenDetail({
        screenId,
        ...(locale === undefined ? {} : { locale }),
        ...(pjNumber === undefined ? {} : { pj: { number: pjNumber, side } }),
      }),
    enabled: Number.isInteger(screenId) && screenId > 0 && enabled,
  });
}
